import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function EditCustomer() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
  });
  const [originalPhone, setOriginalPhone] = useState('');
  const [duplicate, setDuplicate] = useState(null);
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/customers/${id}`);
        if (response.ok) {
          const customer = await response.json();
          setFormData({
            firstName: customer.firstName || '',
            lastName: customer.lastName || '',
            phoneNumber: customer.phoneNumber || '',
            email: customer.email || '',
          });
          setOriginalPhone(customer.phoneNumber || '');
        }
      } catch (error) {
        console.error('Feil ved henting av kunden:', error);
      }
    };
    fetchCustomer();
  }, [id]);

  // Debounced duplicate check when phone changes
  useEffect(() => {
    const phone = formData.phoneNumber.trim();

    // No check needed if unchanged from original
    if (phone === originalPhone) {
      setDuplicate(null);
      return;
    }

    clearTimeout(debounceRef.current);
    setDuplicate(null);

    if (phone.replace(/\D/g, '').length < 8) return;

    setPhoneChecking(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/customers/check-phone?phone=${encodeURIComponent(phone)}&excludeId=${id}`
        );
        const data = await res.json();
        setDuplicate(data || null);
      } catch {
        setDuplicate(null);
      } finally {
        setPhoneChecking(false);
      }
    }, 500);
  }, [formData.phoneNumber, originalPhone, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'phoneNumber') setSaveError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (duplicate) {
      const confirmed = window.confirm(
        `Telefonnummeret er allerede registrert på ${duplicate.firstName} ${duplicate.lastName}. Lagre likevel?`
      );
      if (!confirmed) return;
    }

    const trimmedEmail = formData.email.trim();
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setSaveError('E-post må enten stå tomt eller være en gyldig adresse (f.eks. navn@eksempel.no).');
      return;
    }

    setSaving(true);
    setSaveError('');

    try {
      const response = await fetch(`${API_BASE_URL}/customers/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          email: trimmedEmail,
          lastModified: new Date().toLocaleString('no-NO', { timeZone: 'Europe/Oslo' }),
        }),
      });

      if (response.ok) {
        navigate(`/customer-details/${id}`);
      } else {
        const err = await response.json().catch(() => ({}));
        setSaveError(err.message || 'Feil ved lagring.');
      }
    } catch {
      setSaveError('Kunne ikke nå serveren.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto py-8">
      <h2 className="text-2xl font-bold mb-6 text-center">Rediger Kunde</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Fornavn:</label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Etternavn:</label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Telefonnummer:</label>
          <div className="relative mt-1">
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              required
              className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none sm:text-sm ${
                duplicate
                  ? 'border-amber-400 focus:ring-amber-400 focus:border-amber-400'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
            />
            {phoneChecking && (
              <div className="absolute right-3 top-2.5">
                <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          {duplicate && (
            <div className="mt-2 p-3 rounded-lg bg-amber-50 border border-amber-300 flex items-start gap-2">
              <span className="text-amber-500 text-lg leading-none">⚠</span>
              <div className="text-sm text-amber-800">
                <p className="font-semibold">Duplikat oppdaget</p>
                <p>
                  Dette nummeret er allerede registrert på{' '}
                  <Link
                    to={`/customer-details/${duplicate._id}`}
                    className="font-medium underline hover:text-amber-900"
                  >
                    {duplicate.firstName} {duplicate.lastName}
                  </Link>
                  . Du kan fortsette å lagre, men vurder å{' '}
                  <Link
                    to={`/merge-customers/${id}/${duplicate._id}`}
                    className="underline hover:text-amber-900"
                  >
                    slå sammen kundekortene
                  </Link>{' '}
                  i stedet.
                </p>
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Epost:</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        {saveError && (
          <p className="text-sm text-red-600 text-center">{saveError}</p>
        )}

        <div className="flex items-center justify-center gap-4 pt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-5 py-2.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm"
          >
            Avbryt
          </button>
          <button
            type="submit"
            disabled={saving || phoneChecking}
            className={`px-6 py-2.5 rounded text-white text-sm font-semibold ${
              duplicate
                ? 'bg-amber-500 hover:bg-amber-600'
                : 'bg-green-500 hover:bg-green-600'
            } disabled:opacity-50`}
          >
            {saving ? 'Lagrer…' : duplicate ? 'Lagre likevel' : 'Lagre endringer'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditCustomer;
